import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  useMediaQuery,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { getConsumer } from "../../redux/action/otp";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getMoveService } from "../../redux/action/move-service";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { CheckCircleOutline } from "@mui/icons-material";

interface ConsumerMoveSummaryProps {
  onMoveSelect: (moveData: any) => void;
  selectedMoveServiceData: any;
  user: any;
}

const ConsumerMoveSummary: React.FC<ConsumerMoveSummaryProps> = ({
  onMoveSelect,
  selectedMoveServiceData,
  user,
}) => {
  const theme = useTheme();
  const { canonicalId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [moveServices, setMoveServices] = useState<any[]>([]);
  const [selectedMoveOrder, setSelectedMoveOrder] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [moveId, setMoveId] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [selectedMove, setSelectedMove] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { carrierCanonicalId } = useParams();
  const [error, setError] = useState("");

  useEffect(() => {
    if (!carrierCanonicalId) {
      setError("Carrier ID is missing");
      return;
    }
    if (!canonicalId) {
      setError("user id is missing");
      return;
    }
    if (canonicalId) {
      dispatch<any>(getConsumer({ canonicalId, carrierCanonicalId }))
        .then(unwrapResult)
        .then((res: any) => {
          setMoveServices(res.moveServices || []);
          setMoveId(res.moveServices[0]?.canonical_id || "");
          if (res.moveServices && res.moveServices.length > 0) {
            setSelectedMove(res.moveServices[0]);
            setSelectedMoveOrder(res.moveServices[0]?.move_order_number || "");
            onMoveSelect(res.moveServices[0]);
            setCurrentStep(res.moveServices[0]?.last_step || null);
          }
        })
        .catch((err: any) => {
          console.error("Failed to fetch move data:", err);
        });
    }
  }, [canonicalId]);

  useEffect(() => {
    if (moveId) {
      dispatch<any>(getMoveService(moveId))
        .then(unwrapResult)
        .then((res: any) => {
          setBusinessName(res?.carrier?.business_name || "");
        })
        .catch((err: any) => {
          console.error("Failed to fetch move data:", err);
        });
    }
  }, [moveId]);

  const [moveCanonicalId, setMoveCanonicalId] = useState("");

  const handleMoveSelection = (move: any) => {
    setSelectedMoveOrder(move?.move_order_number || "");
    setSelectedMove(move);
    onMoveSelect(move);
    setCurrentStep(move?.last_step || null);
    setMoveCanonicalId(move?.canonical_id || "");
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const calculateRemainingCost = () => {
    if (!selectedMoveServiceData) return 0;

    const price = parseFloat(selectedMoveServiceData?.price || 0);
    const packingPrice = parseFloat(selectedMoveServiceData?.packingPrice || 0);
    const initialDeposit = parseFloat(
      selectedMoveServiceData?.initial_deposit || 0
    );

    return price + packingPrice - initialDeposit;
  };

  const navigateToLastStep = (lastStep: number) => {
    if (selectedMoveServiceData?.status === "cancelled") return;

    switch (lastStep) {
      case 1:
        navigate(`/${businessName}/choose-service`);
        break;
      case 2:
        navigate(
          `/${businessName}/${carrierCanonicalId}/delivery-details/${moveCanonicalId}`
        );
        break;
      case 3:
        navigate(
          `/${businessName}/${carrierCanonicalId}/apartment-info/${moveCanonicalId}`
        );
        break;
      case 4:
        navigate(
          `/${businessName}/${carrierCanonicalId}/bulky-items/${moveCanonicalId}`
        );
        break;
      case 5:
        navigate(
          `/${businessName}/${carrierCanonicalId}/insurance/${moveCanonicalId}`
        );
        break;
      case 6:
        navigate(
          `/${businessName}/${carrierCanonicalId}/user-details/${moveCanonicalId}`
        );
        break;
      case 7:
        navigate(
          `/${businessName}/${carrierCanonicalId}/storage/${moveCanonicalId}`
        );
        break;
      case 8:
        navigate(
          `/${businessName}/${carrierCanonicalId}/upload-items/${moveCanonicalId}`
        );
        break;
      case 9:
        navigate(
          `/${businessName}/${carrierCanonicalId}/inventory/${moveCanonicalId}`
        );
        break;
      case 10:
        navigate(
          `/${businessName}/${carrierCanonicalId}/move-price/${moveCanonicalId}`
        );
        break;
      case 11:
        navigate(
          `/${businessName}/${carrierCanonicalId}/move-summary/${moveCanonicalId}`
        );
        break;
      default:
        navigate(`/${businessName}/choose-service`);
        break;
    }
  };

  const isMoveCancelled = selectedMoveServiceData?.status === "cancelled";
  const extractDatePart = (datetime: string): string => {
    return datetime.split("T")[0];
  };
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          paddingTop: "20px",
        }}
      >
        <Box sx={{ width: isMobile ? "100%" : "70%" }}>
          <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  Move Summary
                </Typography>
                {isMoveCancelled && (
                  <Chip
                    label="Move Cancelled"
                    color="error"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      height: "32px",
                    }}
                  />
                )}
              </Box>

              {currentStep !== null && (
                <Box
                  sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigateToLastStep(currentStep as number)}
                    disabled={isMoveCancelled}
                    sx={{
                      borderRadius: "8px",
                      padding: "12px 24px",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderColor: isMoveCancelled
                        ? "#ccc"
                        : theme.palette.primary.main,
                      color: isMoveCancelled
                        ? "#666"
                        : theme.palette.primary.main,
                      backgroundColor: isMoveCancelled ? "#f5f5f5" : "#f5f9ff",
                      "&:hover": {
                        backgroundColor: isMoveCancelled
                          ? "#f5f5f5"
                          : theme.palette.primary.light,
                        borderColor: isMoveCancelled
                          ? "#ccc"
                          : theme.palette.primary.dark,
                      },
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                      maxWidth: "500px",
                      boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                      position: "relative",
                      overflow: "hidden",
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: "4px",
                        backgroundColor: isMoveCancelled
                          ? "#ccc"
                          : theme.palette.primary.main,
                      },
                    }}
                    startIcon={
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                          stroke={
                            isMoveCancelled
                              ? "#666"
                              : theme.palette.primary.main
                          }
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 8V12L15 15"
                          stroke={
                            isMoveCancelled
                              ? "#666"
                              : theme.palette.primary.main
                          }
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                  >
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ fontSize: isMobile ? "10px" : "15px" }}
                      >
                        {isMoveCancelled
                          ? "Move Cancelled - Cannot Continue"
                          : "Do you want to continue this move"}
                      </Typography>
                    </Box>
                  </Button>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Current Moves
                </Typography>
                <Box
                  sx={{
                    height: moveServices.length > 5 ? "400px" : "auto",
                    overflowY: "auto",
                  }}
                >
                  {selectedMove && (
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {moveServices.map((move, index) => (
                        <Button
                          key={index}
                          variant="outlined"
                          onClick={() =>
                            move?.status !== "cancelled" &&
                            handleMoveSelection(move)
                          }
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 20px",
                            borderColor:
                              move?.move_order_number === selectedMoveOrder
                                ? move?.status === "cancelled"
                                  ? "#ccc"
                                  : theme.palette.primary.main
                                : "#ccc",
                            color:
                              move?.move_order_number === selectedMoveOrder
                                ? move?.status === "cancelled"
                                  ? "#666"
                                  : theme.palette.primary.main
                                : "#000",
                            backgroundColor:
                              move?.status === "cancelled"
                                ? "#f5f5f5"
                                : "transparent",
                            "&:hover": {
                              backgroundColor:
                                move?.status === "cancelled"
                                  ? "#f5f5f5"
                                  : theme.palette.action.hover,
                            },
                            transition: "all 0.3s ease",
                            position: "relative",
                            opacity: move?.status === "cancelled" ? 0.8 : 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {move?.move_order_number
                                ? `Move #${move.move_order_number}`
                                : "Move"}
                            </Typography>
                            {move?.status === "cancelled" && (
                              <Chip
                                label="Cancelled"
                                size="small"
                                color="error"
                                sx={{
                                  ml: 1,
                                  height: "20px",
                                  fontSize: "0.65rem",
                                }}
                              />
                            )}
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  move?.status === "cancelled"
                                    ? "#999"
                                    : theme.palette.text.secondary,
                                fontStyle: "italic",
                              }}
                            >
                              {move?.created_at
                                ? new Date(move.created_at).toLocaleDateString(
                                    "en-US",
                                    {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    }
                                  )
                                : "Date not available"}
                            </Typography>

                            {move?.move_order_number === selectedMoveOrder && (
                              <CheckCircleOutline
                                fontSize="small"
                                sx={{
                                  color:
                                    move?.status === "cancelled"
                                      ? "#999"
                                      : theme.palette.primary.main,
                                }}
                              />
                            )}
                          </Box>
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              {selectedMoveServiceData && (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Customer Information
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      opacity: isMoveCancelled ? 0.7 : 1,
                    }}
                  >
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">First Name</Typography>
                      <Typography variant="body1">
                        {user?.first_name ? user?.first_name : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Last Name</Typography>
                      <Typography variant="body1">
                        {user?.last_name ? user?.last_name : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Phone Number</Typography>
                      <Typography variant="body1">
                        {user?.phone_number
                          ? `+1${user.phone_number}`
                          : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Order ID</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.move_order_number
                          ? `${selectedMoveServiceData.move_order_number}`
                          : "Not Available"}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Move Details
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      opacity: isMoveCancelled ? 0.7 : 1,
                    }}
                  >
                    <Box sx={{ flex: "1 1 100%" }}>
                      <Typography variant="body2">Moving From</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.moving_from
                          ? selectedMoveServiceData?.moving_from
                          : "Not Available"}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: "1 1 100%" }}>
                      <Typography variant="body2">Moving To</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.moving_to
                          ? selectedMoveServiceData?.moving_to
                          : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Move Type</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.move_type
                          ? selectedMoveServiceData?.move_type
                          : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">
                        Number of Bedrooms
                      </Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.number_of_bedrooms
                          ? `${selectedMoveServiceData.number_of_bedrooms}`
                          : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Distance</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.distance
                          ? `${selectedMoveServiceData.distance} miles`
                          : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Moving Date</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.moving_date
                          ? extractDatePart(
                              selectedMoveServiceData?.moving_date.toString()
                            )
                          : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">
                        {parseFloat(selectedMoveServiceData?.distance || "0") >
                        200
                          ? "Total Cubic Feet"
                          : "Pickup Square Footage"}
                      </Typography>
                      <Typography variant="body1">
                        {parseFloat(selectedMoveServiceData?.distance || "0") >
                        200
                          ? selectedMoveServiceData?.total_cubic_feet ||
                            "Not Available"
                          : selectedMoveServiceData?.pickup_square_footage ||
                            "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Price</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.price
                          ? `$${selectedMoveServiceData.price}`
                          : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Deposit Paid</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.initial_deposit
                          ? `$${selectedMoveServiceData.initial_deposit}`
                          : "Not Paid"}
                      </Typography>
                    </Box>
                    {selectedMoveServiceData?.isPackingRequired ? (
                      <Box
                        sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                      >
                        <Typography variant="body2">Packing Price</Typography>
                        <Typography variant="body1">
                          {selectedMoveServiceData?.packingPrice
                            ? `$${selectedMoveServiceData.packingPrice}`
                            : "No Packing"}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Payment Summary
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      opacity: isMoveCancelled ? 0.7 : 1,
                    }}
                  >
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Total Cost</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.price ||
                        selectedMoveServiceData?.packingPrice
                          ? `$${(
                              parseFloat(
                                selectedMoveServiceData?.price || "0"
                              ) +
                              parseFloat(
                                selectedMoveServiceData?.packingPrice || "0"
                              )
                            ).toFixed(2)}`
                          : "Not Available"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 16px)", minWidth: "200px" }}
                    >
                      <Typography variant="body2">Deposit Paid</Typography>
                      <Typography variant="body1">
                        {selectedMoveServiceData?.initial_deposit
                          ? `$${selectedMoveServiceData.initial_deposit}`
                          : "$0.00"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "1 1 100%",
                        minWidth: "200px",
                        backgroundColor: isMoveCancelled
                          ? "#f5f5f5"
                          : theme.palette.grey[100],
                        padding: 2,
                        borderRadius: 1,
                        borderLeft: `4px solid ${
                          isMoveCancelled ? "#ccc" : theme.palette.primary.main
                        }`,
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        Remaining Balance
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        ${calculateRemainingCost().toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default ConsumerMoveSummary;
